import { useCallback, useState } from 'react';

import type { HotelPlaceOption } from '@voll-intelligence/genui-contract';
import type { TravelerSearchResult } from '../../../shared/schema/traveler_directory.js';

/** O catálogo que NUNCA esquece (viajantes e lugares já vistos), em estado: cada
 *  lembrança devolve um Map novo, o último visto de cada id por cima. */
export type Memory<T> = ReadonlyMap<string, T>;

export const travelerIdOf = (traveler: TravelerSearchResult): string => traveler.id;
export const placeIdOf = (place: HotelPlaceOption): string => place.placeId;

export function withRemembered<T>(
  memory: Memory<T>,
  items: readonly T[],
  idOf: (item: T) => string,
): Memory<T> {
  if (items.length === 0) return memory;
  const next = new Map(memory);
  for (const item of items) next.set(idOf(item), item);
  return next;
}

/** Algum dos itens ainda não está na memória (por id)? */
const forgetsAny = <T>(memory: Memory<T>, items: readonly T[], idOf: (item: T) => string) =>
  items.some((item) => !memory.has(idOf(item)));

/** A memória da barra: os viajantes e os lugares já vistos, que `resolveValue`
 *  precisa resolver mesmo depois de saírem da exibição. Busca, sugestões e Minha
 *  localização lembram na chegada (`rememberTravelers`/`rememberPlaces`); os
 *  semeados pelo bloco (`seededTravelers`, `initialPlace`) entram no render em que
 *  aparecem. Fora da `useHotelSearchOptions` pelo teto de linhas dela. */
export function useSearchMemory(
  seededTravelers: readonly TravelerSearchResult[],
  initialPlace: HotelPlaceOption | undefined,
) {
  const [travelerMemory, setTravelerMemory] = useState<Memory<TravelerSearchResult>>(
    () => new Map(),
  );
  const [placeMemory, setPlaceMemory] = useState<Memory<HotelPlaceOption>>(() => new Map());
  if (forgetsAny(travelerMemory, seededTravelers, travelerIdOf)) {
    setTravelerMemory((memory) => withRemembered(memory, seededTravelers, travelerIdOf));
  }
  const initialPlaces = initialPlace ? [initialPlace] : [];
  if (forgetsAny(placeMemory, initialPlaces, placeIdOf)) {
    setPlaceMemory((memory) => withRemembered(memory, initialPlaces, placeIdOf));
  }
  const rememberTravelers = useCallback((travelers: readonly TravelerSearchResult[]) => {
    setTravelerMemory((memory) => withRemembered(memory, travelers, travelerIdOf));
  }, []);
  const rememberPlaces = useCallback((places: readonly HotelPlaceOption[]) => {
    setPlaceMemory((memory) => withRemembered(memory, places, placeIdOf));
  }, []);
  return { travelerMemory, placeMemory, rememberTravelers, rememberPlaces };
}
